<template>
  <div class="vue-materials-slider">
    <vue-slider v-model="value" v-bind="options">
      <template slot="tooltip" slot-scope="{ value }">
        <div class="diy-tooltip">
          <span v-if="updatedValue">
            <span v-if="tooltipValueText">{{ tooltipValueText }}</span>
            <span v-else>{{ tooltipValue }}</span>
          </span>
        </div>
      </template>
    </vue-slider>
  </div>
</template>

<script>
import vueSlider from 'vue-slider-component';
import setInputValue from "../utils/setInputValue";

/* E.g.
<div class="vue-app">
  <slider :labels="[ [0, 'The banks will have the same accuracy'] ]" interval="5" max="5"
    max-text="Bank B will be much more accurate than Bank A." min="-5"
    min-text="Bank A will be much more accurate than Bank B." target="benchmarking2">Loading slider...
  </slider>
</div>
*/

export default {
  props: {
    interval: {
      type: String,
      default: '1',
    },
    labels: {
      type: String,
      default: '',
    },
    min: {
      type: String,
      default: '0',
    },
    max: {
      type: String,
      default: '100',
    },
    default: {
      type: Number,
      required: false
    },
    tooltipFn: {
      type: Function,
      required: false,
    },
    tooltipPrefix: {
      type: String,
      default: '',
    },
    tooltipSuffix: {
      type: String,
      default: '',
    },
    minText: {
      type: String,
      required: false
    },
    maxText: {
      type: String,
      required: false
    },
  },
  name: 'Slider',
  data() {
    return {
      updatedValue: true,
      value: parseFloat((this.default || this.min).toString()),
      options: {
        min: parseFloat(this.min.toString()),
        max: parseFloat(this.max.toString()),
        interval: parseFloat(this.interval.toString()),
        // dotSize: 30,
        dotWidth: 30,
        dotHeight: 30,
      },
    }
  },
  mounted() {
    setInputValue(this.target, this.value);
    checkconditions();
  },
  watch: {
    value(newVal) {
      this.updatedValue = true;
      setInputValue(this.target, newVal);
      checkconditions();
    }
  },
  computed: {
    slideLabels() {
      const labels = {};
      if (this.minText !== '')
        labels[this.options.min] = this.minText;

      if (this.maxText !== '')
        labels[this.options.max] = this.maxText;

      // this.labels.forEach((label) => {
      //   labels[ label[ 0 ] ] = label[ 1 ];
      // });

      return labels;
    },
    tooltipValue() {
      const value = this.tooltipFn ? this.tooltipFn(this.value) : this.value;
      return this.tooltipPrefix + value + this.tooltipSuffix;
    },
    tooltipValueText() {
      if (this.minText !== '' && this.value === this.options.min) {
        return this.minText;
      } else if (this.maxText !== '' && this.value === this.options.max) {
        return this.maxText;
      }

      const labels = this.slideLabels;

      if (labels[this.value])
        return labels[this.value];

      return '';
    },
  },
  components: {
    vueSlider
  }
}
</script>

<style lang="scss" scoped>
.vue-materials-slider {
  /deep/ {
    .vue-slider .diy-tooltip span {
      display: inline-block;
      text-align: center;
      min-width: 200px;
    }

    .vue-slider-component .vue-slider-tooltip-wrap.vue-slider-tooltip-top {
      top: 50px;
      font-size: 15px;
    }

    /* component style */
    .vue-slider-disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    /* rail style */
    .vue-slider-rail {
      background-color: #ccc;
      border-radius: 15px;
    }

    /* process style */
    .vue-slider-process {
      background-color: #3498db;
      border-radius: 15px;
    }

    /* mark style */
    .vue-slider-mark {
      z-index: 4;
    }

    .vue-slider-mark:first-child .vue-slider-mark-step, .vue-slider-mark:last-child .vue-slider-mark-step {
      display: none;
    }

    .vue-slider-mark-step {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background-color: rgba(0, 0, 0, 0.16);
    }

    .vue-slider-mark-label {
      font-size: 14px;
      white-space: nowrap;
    }

    /* dot style */

    .vue-slider-dot {
      width: 40px !important;
      height: 25px !important;
    }

    .vue-slider-dot-handle {
      cursor: pointer;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background-color: #fff;
      box-sizing: border-box;
      box-shadow: 0.5px 0.5px 2px 1px rgba(0, 0, 0, 0.32);
    }

    .vue-slider-dot-handle-focus {
      box-shadow: 0px 0px 1px 2px rgba(52, 152, 219, 0.36);
    }

    .vue-slider-dot-handle-disabled {
      cursor: not-allowed;
      background-color: #ccc;
    }

    .vue-slider-dot-tooltip-inner {
      font-size: 14px;
      white-space: nowrap;
      padding: 2px 5px;
      min-width: 20px;
      text-align: center;
      color: #fff;
      border-radius: 5px;
      border-color: #3498db;
      background-color: #3498db;
      box-sizing: content-box;
    }

    .vue-slider-dot-tooltip-inner::after {
      content: "";
      position: absolute;
    }

    .vue-slider-dot-tooltip-inner-top::after {
      top: 100%;
      left: 50%;
      transform: translate(-50%, 0);
      height: 0;
      width: 0;
      border-color: transparent;
      border-style: solid;
      border-width: 5px;
      border-top-color: inherit;
    }

    .vue-slider-dot-tooltip-inner-bottom::after {
      bottom: 100%;
      left: 50%;
      transform: translate(-50%, 0);
      height: 0;
      width: 0;
      border-color: transparent;
      border-style: solid;
      border-width: 5px;
      border-bottom-color: inherit;
    }

    .vue-slider-dot-tooltip-inner-left::after {
      left: 100%;
      top: 50%;
      transform: translate(0, -50%);
      height: 0;
      width: 0;
      border-color: transparent;
      border-style: solid;
      border-width: 5px;
      border-left-color: inherit;
    }

    .vue-slider-dot-tooltip-inner-right::after {
      right: 100%;
      top: 50%;
      transform: translate(0, -50%);
      height: 0;
      width: 0;
      border-color: transparent;
      border-style: solid;
      border-width: 5px;
      border-right-color: inherit;
    }

    .vue-slider-dot-tooltip-wrapper {
      opacity: 0;
      transition: all 0.3s;
    }

    .vue-slider-dot-tooltip-wrapper-show {
      opacity: 1;
    }

    .vue-slider-dot-tooltip {
      visibility: visible;
    }
  }
}
</style>
